import type { ServiceLine } from "~/providers/OrgConfigProvider";

// A discharge/coordinate "provider" is just a display name string in the demo
// prototype. To let some providers flow into the real Intake queue (and on to
// scheduling) while others stay pure-referral, a provider opts in only when its
// name starts with this sentinel prefix. The remainder of the name is then
// fuzzy-matched against the org's configured service lines, so which service
// line a provider resolves to can be changed by renaming a service line in the
// environment — no code deploy required.
export const INTAKE_PROVIDER_PREFIX = "Metropolitan";

const norm = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Strip the opt-in prefix and return the remaining tokens, or null when the
// provider name does not start with the sentinel prefix (i.e. does not opt in).
function optInRemainder(providerName: string): string | null {
  const lower = norm(providerName);
  const pfx = norm(INTAKE_PROVIDER_PREFIX);
  if (lower === pfx) return "";
  if (lower.startsWith(`${pfx} `)) return lower.slice(pfx.length).trim();
  return null;
}

/**
 * Resolve the service line an opted-in discharge provider maps to, or undefined
 * when the provider does not opt in, no configured service line matches its
 * remainder, or the best match is ambiguous. Matching is by shared word tokens
 * (best overlap wins), so e.g. "Metropolitan Hospital at Home" resolves to the
 * "Hospital at Home" service line. When two or more service lines tie for the
 * top score we return undefined rather than guess — the remainder is too
 * generic to map (e.g. "home care" sharing only "home"/"care"). Name the
 * opt-in provider after its target service line to resolve unambiguously.
 */
export function resolveIntakeServiceLine(
  providerName: string | undefined,
  serviceLines: Record<string, ServiceLine | undefined>,
): ServiceLine | undefined {
  if (!providerName) return undefined;
  const remainder = optInRemainder(providerName);
  if (remainder === null) return undefined;

  const wanted = new Set(remainder.split(" ").filter(Boolean));
  if (wanted.size === 0) return undefined;

  const lines = Object.values(serviceLines).filter(
    (sl): sl is ServiceLine => sl !== undefined,
  );
  const ranked = lines
    .map((sl) => ({
      sl,
      score: norm(sl.name)
        .split(" ")
        .filter((token) => wanted.has(token)).length,
    }))
    .filter((match) => match.score > 0)
    .sort((a, b) => b.score - a.score);

  if (ranked.length === 0) return undefined;
  // Ambiguous: two or more lines tie for the best overlap. Don't guess.
  if (ranked.length > 1 && ranked[0].score === ranked[1].score) return undefined;
  return ranked[0].sl;
}

/**
 * Whether a service line is a Hospital at Home line, matched by the names the
 * program goes by across customers. Shared so every surface that needs to
 * recognise the program (scheduler episode creation, the active patients list)
 * agrees on the same set of aliases.
 */
export function isHospitalAtHomeServiceLine(serviceLineName: string): boolean {
  const lower = serviceLineName.toLowerCase();
  return (
    lower.includes("hospital at home") ||
    lower.includes("hcah") ||
    lower.includes("h@h") ||
    lower.includes("acute care at home") ||
    lower.includes("acute home")
  );
}

/** Whether a provider name is eligible to be routed into the Intake queue. */
export function isIntakeEligibleProvider(
  providerName: string | undefined,
  serviceLines: Record<string, ServiceLine | undefined>,
): boolean {
  return resolveIntakeServiceLine(providerName, serviceLines) !== undefined;
}
